import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { withStyles } from '@mui/styles';
import Typography from '@mui/material/Typography';
import NoSsr from '@mui/material/NoSsr';
import TextField from '@mui/material/TextField';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import { emphasize } from '@mui/material/styles';

const styles = theme => ({
  root: {
    flexGrow: 1
  },
  input: {
    display: 'flex',
    padding: 2,
    height: 'auto'
  },
  valueContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    flex: 1,
    alignItems: 'center',
  },
  chip: {
    margin: '4px 2px',
    border: '1px solid #555555'
  },
  chipFocused: {
    backgroundColor: emphasize(
      theme.palette.mode === 'light' ? theme.palette.grey[300] : theme.palette.grey[700],
      0.08,
    )
  },
  noOptionsMessage: {
    padding: `${theme.spacing(1)} ${theme.spacing(2)}`
  },
  singleValue: {
    fontSize: 16
  },
  placeholder: {
    position: 'absolute',
    left: 2,
    fontSize: 16
  },
  paper: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2)
  },
});

function NoOptionsMessage(props) {
  return (
    <Typography
      color="textSecondary"
      className={props.selectProps.classes.noOptionsMessage}
      {...props.innerProps}
    >
      {props.children}
    </Typography>
  );
}

const inputComponent = React.forwardRef((props, ref) =>
  <div ref={ref} {...props} />)

function Control(props) {
  return (
    <TextField
      variant="standard"
      fullWidth
      name={props.selectProps.name}
      InputProps={{
        inputComponent,
        inputProps: {
          className: props.selectProps.classes.input,
          inputRef: props.innerRef,
          children: props.children,
          ...props.innerProps
        }
      }}
      {...props.selectProps.textFieldProps} />
  );
}

function Option(props) {
  return (
    <MenuItem
      ref={props.innerRef}
      selected={props.isFocused}
      component="div"
      style={{
        fontWeight: props.isSelected ? 500 : 400
      }}
      {...props.innerProps}
    >
      {props.children}
    </MenuItem>
  );
}

function Placeholder(props) {
  return (
    <Typography
      color="textSecondary"
      className={props.selectProps.classes.placeholder}
      {...props.innerProps}
    >
      {props.children}
    </Typography>
  );
}

function ValueContainer(props) {
  return <div className={props.selectProps.classes.valueContainer}>{props.children}</div>;
}

function MultiValue(props) {
  return (
    <Chip
      tabIndex={-1}
      label={props.children}
      className={classNames(props.selectProps.classes.chip, {
        [props.selectProps.classes.chipFocused]: props.isFocused
      })}
      onDelete={event => {
        props.removeProps.onClick();
        props.removeProps.onMouseDown(event);
      }}
    />
  );
}

function Menu(props) {
  return (
    <Paper square className={props.selectProps.classes.paper} {...props.innerProps}>
      {props.children}
    </Paper>
  );
}

const components = {
  Option,
  Control,
  NoOptionsMessage,
  Placeholder,
  MultiValue,
  ValueContainer,
  Menu
};

class Autocomplete extends Component {

  /**
   *   Allow retention of previously selected values which may have been deleted.
   *   Aura only allows one selection.
   */
  handleChange = selected => {
    const { suggestions, onChange, suggestionKey, value, aura } = this.props;
    if (aura && selected.length > 1) {
      alert('only one AURA is allowed');
      onChange([]);
      selected = [selected[0]];
      const values = selected.map(sel => {
        const val = suggestions.find(s => s[suggestionKey] === sel.value);
        return val || value.find(v => v.name === sel.value);
      });
      onChange(values)
    } else if (selected) {
      const values = selected.map(sel => {
        const val = suggestions.find(s => s[suggestionKey] === sel.value);
        return val || value.find(v => v.name === sel.value) ||
          (this.props.allowCreate ? { name: sel.label || sel.value, value: sel.value } : null);
      }).filter(Boolean);
      onChange(values);
    } else {
      onChange([]);
    }
  };

  render() {
    const {
      name,
      theme,
      label,
      classes,
      disabled,
      keepOpen,
      placeholder,
      suggestionKey
    } = this.props;

    let value = '';
    if (this.props.value && this.props.value.length > 0) {
      value = this.props.value
        .map(v => ({
          value: v[suggestionKey],
          label: v[suggestionKey]
        }));
    }

    let suggestions = [];
    if (this.props.suggestions && this.props.suggestions.length > 0) {
      suggestions = this.props.suggestions
        .map(suggestion => ({
          value: suggestion[suggestionKey],
          label: suggestion[suggestionKey]
        }));
    }

    const SelectComponent = this.props.allowCreate ? CreatableSelect : Select;
    const selectStyles = {
      input: base => ({
        ...base,
        color: theme.palette.text.primary
      })
    };

    // warning: failed prop type inputComponent
    return (
      <div className={classes.root}>
        <NoSsr>
          <SelectComponent
            classes={classes}
            isDisabled={disabled}
            styles={selectStyles}
            textFieldProps={{
              label,
              InputLabelProps: {
                shrink: true
              }
            }}
            options={suggestions}
            components={components}
            name={name}
            value={value}
            onChange={this.handleChange}
            placeholder={placeholder}
            isMulti
            closeMenuOnSelect={!keepOpen}
          />
        </NoSsr>
      </div>
    );
  }
}

Autocomplete.defaultProps = {
  placeholder: null,
  disabled: false,
  allowCreate: false
};

Autocomplete.propTypes = {
  classes: PropTypes.object.isRequired,
  theme: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.arrayOf(PropTypes.object).isRequired,
  suggestions: PropTypes.arrayOf(PropTypes.object).isRequired,
  suggestionKey: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  allowCreate: PropTypes.bool,
  keepOpen: PropTypes.bool,
  aura: PropTypes.bool,
};

export default withStyles(styles, { withTheme: true })(Autocomplete);
